import { z } from 'zod';
import {
  acaoSaldoTituloSchema,
  dataHoraSchema,
  dinheiroSchema,
  filtroAtivoSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Cadastros financeiros (`.spec/016`, seção 6).
 *
 * Quatro cadastros de apoio: plano de contas, histórico bancário, histórico
 * financeiro e conta corrente. Nenhum guarda movimento — todos existem para
 * classificar o que a movimentação de conta corrente e a baixa de título vão
 * registrar.
 *
 * Ficam num arquivo só, e não em quatro, porque são pequenos e sempre lidos
 * juntos: o histórico financeiro aponta para o bancário, e a movimentação usa
 * conta corrente e conta contábil na mesma linha.
 */

/** Descrição de cadastro: obrigatória, aparada, com teto de tamanho. */
const descricaoSchema = z.string().trim().min(2, 'Informe a descrição.').max(200);

/** Campos de auditoria que toda resposta de cadastro devolve. */
const camposDeResposta = {
  id: uuidSchema,
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
};

/* -------------------------------------------------------------------------- */
/* Conta contábil                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Classificação contábil da conta.
 *
 * Os cinco domínios abaixo são **derivados do código** (ver `derivarClassificacao`),
 * mas gravados em coluna e editáveis. Derivar na leitura seria mais enxuto e
 * está errado para os casos que existem de verdade: conta redutora de ativo tem
 * natureza credora dentro de um grupo devedor, e um plano herdado pode não
 * seguir a convenção de faixa numérica nenhuma.
 */
export const TIPOS_CONTA_CONTABIL = ['SINTETICA', 'ANALITICA'] as const;
export const tipoContaContabilSchema = z.enum(TIPOS_CONTA_CONTABIL);
export type TipoContaContabil = z.infer<typeof tipoContaContabilSchema>;

export const GRUPOS_CONTA_CONTABIL = ['ATIVO', 'PASSIVO', 'RECEITA', 'CUSTO', 'DESPESA'] as const;
export const grupoContaContabilSchema = z.enum(GRUPOS_CONTA_CONTABIL);
export type GrupoContaContabil = z.infer<typeof grupoContaContabilSchema>;

export const NATUREZAS_CONTA_CONTABIL = ['DEVEDORA', 'CREDORA'] as const;
export const naturezaContaContabilSchema = z.enum(NATUREZAS_CONTA_CONTABIL);
export type NaturezaContaContabil = z.infer<typeof naturezaContaContabilSchema>;

export const DEMONSTRATIVOS_CONTA_CONTABIL = ['BALANCO', 'DRE'] as const;
export const demonstrativoContaContabilSchema = z.enum(DEMONSTRATIVOS_CONTA_CONTABIL);
export type DemonstrativoContaContabil = z.infer<typeof demonstrativoContaContabilSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_TIPO_CONTA_CONTABIL: Readonly<Record<TipoContaContabil, string>> = {
  SINTETICA: 'Sintética',
  ANALITICA: 'Analítica',
};

export const ROTULO_GRUPO_CONTA_CONTABIL: Readonly<Record<GrupoContaContabil, string>> = {
  ATIVO: 'Ativo',
  PASSIVO: 'Passivo e patrimônio líquido',
  RECEITA: 'Receitas',
  CUSTO: 'Custos das obras',
  DESPESA: 'Despesas',
};

export const ROTULO_NATUREZA_CONTA_CONTABIL: Readonly<Record<NaturezaContaContabil, string>> = {
  DEVEDORA: 'Devedora',
  CREDORA: 'Credora',
};

export const ROTULO_DEMONSTRATIVO_CONTA_CONTABIL: Readonly<
  Record<DemonstrativoContaContabil, string>
> = {
  BALANCO: 'Balanço Patrimonial',
  DRE: 'DRE',
};

/**
 * Nível a partir do qual a conta recebe lançamento.
 *
 * Quatro é a convenção do plano padrão (`1.1.1.01`). Acima disso a conta só
 * agrupa — lançar em conta sintética duplicaria o valor no balancete, porque o
 * saldo dela já é a soma das filhas.
 */
export const NIVEL_MINIMO_CONTA_ANALITICA = 4;

/** Teto de níveis. Alinhado com a profundidade máxima que o service percorre. */
export const NIVEL_MAXIMO_CONTA_CONTABIL = 9;

const GRUPO_POR_PREFIXO: Readonly<Record<string, GrupoContaContabil>> = {
  '1': 'ATIVO',
  '2': 'PASSIVO',
  '3': 'RECEITA',
  '4': 'CUSTO',
  '5': 'DESPESA',
};

const NATUREZA_POR_GRUPO: Readonly<Record<GrupoContaContabil, NaturezaContaContabil>> = {
  ATIVO: 'DEVEDORA',
  PASSIVO: 'CREDORA',
  RECEITA: 'CREDORA',
  CUSTO: 'DEVEDORA',
  DESPESA: 'DEVEDORA',
};

const DEMONSTRATIVO_POR_GRUPO: Readonly<Record<GrupoContaContabil, DemonstrativoContaContabil>> = {
  ATIVO: 'BALANCO',
  PASSIVO: 'BALANCO',
  RECEITA: 'DRE',
  CUSTO: 'DRE',
  DESPESA: 'DRE',
};

export interface ClassificacaoContaContabil {
  nivel: number;
  tipo: TipoContaContabil;
  grupo: GrupoContaContabil;
  natureza: NaturezaContaContabil;
  demonstrativo: DemonstrativoContaContabil;
}

/**
 * Classificação a partir do código hierárquico.
 *
 * `4.1.1.01` → nível 4, analítica, custo, devedora, DRE. O nível é a contagem
 * de segmentos, e os outros três saem do primeiro dígito.
 *
 * Devolve `null` quando o código não segue a convenção — código gerado
 * automaticamente é um UUID, e um plano importado de outro sistema pode começar
 * com letra. Nesses casos quem chama mantém o que o usuário escolheu, em vez de
 * inventar uma classificação que estaria errada em silêncio.
 *
 * Fica no pacote de contratos, e não na API, porque o formulário precisa da
 * **mesma** derivação para preencher os campos enquanto se digita: duplicá-la
 * no frontend faria a prévia da tela divergir do que o servidor grava.
 */
export function derivarClassificacao(codigoInterno: string): ClassificacaoContaContabil | null {
  const codigo = codigoInterno.trim();
  if (codigo === '') return null;

  // Só dígitos e pontos, sem segmento vazio. Isto é o que descarta o UUID.
  if (!/^\d+(\.\d+)*$/.test(codigo)) return null;

  const segmentos = codigo.split('.');
  if (segmentos.length > NIVEL_MAXIMO_CONTA_CONTABIL) return null;

  const grupo = GRUPO_POR_PREFIXO[segmentos[0]?.[0] ?? ''];
  if (!grupo) return null;

  return {
    nivel: segmentos.length,
    tipo: segmentos.length >= NIVEL_MINIMO_CONTA_ANALITICA ? 'ANALITICA' : 'SINTETICA',
    grupo,
    natureza: NATUREZA_POR_GRUPO[grupo],
    demonstrativo: DEMONSTRATIVO_POR_GRUPO[grupo],
  };
}

/**
 * Plano de contas, em árvore.
 *
 * `codigoInterno` é **opcional na entrada**: vazio, o service grava o próprio
 * `id` (`.spec/010`). É texto e não número porque a notação é hierárquica
 * (`1.1.01`), e ordenar `1.10` antes de `1.2` é o comportamento correto de
 * plano de contas — quem quiser ordem numérica preenche com zeros à esquerda.
 *
 * Os cinco campos de classificação também são **opcionais na entrada**: em
 * branco, o service os deriva do código. Preenchidos, valem como vieram — é o
 * que permite a conta redutora com natureza invertida.
 */
export const criarContaContabilSchema = z.object({
  codigoInterno: z.string().trim().max(60).prefault(''),
  /**
   * Número curto de digitação, único na conta.
   *
   * Nulo na entrada pede o próximo livre ao service. Existe porque ninguém
   * digita `4.1.2.03` num lançamento — digita `171` e segue.
   */
  codigoReduzido: z.number().int().positive().max(999_999).nullable().prefault(null),
  descricao: descricaoSchema,
  /** Nulo indica conta de primeiro nível. */
  paiId: uuidSchema.nullable().prefault(null),

  nivel: z.number().int().min(1).max(NIVEL_MAXIMO_CONTA_CONTABIL).nullable().prefault(null),
  tipo: tipoContaContabilSchema.nullable().prefault(null),
  grupo: grupoContaContabilSchema.nullable().prefault(null),
  natureza: naturezaContaContabilSchema.nullable().prefault(null),
  demonstrativo: demonstrativoContaContabilSchema.nullable().prefault(null),

  /**
   * O lançamento nesta conta precisa apontar uma obra.
   *
   * Todo o grupo de custos nasce marcado. O campo é gravado e exibido; a recusa
   * do lançamento sem obra entra junto com o módulo que o consome.
   */
  exigeObra: z.boolean().prefault(false),
  observacoes: z.string().trim().max(500).prefault(''),
  ativo: z.boolean().prefault(true),
});

export type CriarContaContabil = z.infer<typeof criarContaContabilSchema>;
export const atualizarContaContabilSchema = criarContaContabilSchema;
export type AtualizarContaContabil = z.infer<typeof atualizarContaContabilSchema>;

export const contaContabilSchema = z.object({
  ...camposDeResposta,
  codigoInterno: z.string(),
  codigoReduzido: z.number().int(),
  descricao: z.string(),
  paiId: uuidSchema.nullable(),
  /**
   * Pai desnormalizado na leitura.
   *
   * A listagem mostra a conta acima sem uma segunda consulta por linha — que,
   * numa página de 25, seriam 25 idas ao banco para exibir uma coluna.
   */
  paiCodigo: z.string().nullable(),
  paiDescricao: z.string().nullable(),

  /**
   * A conta é **folha** da árvore — ninguém a tem como pai.
   *
   * Calculado na leitura, não guardado em coluna: quem determina a resposta é a
   * existência de outra linha, e uma coluna materializada precisaria ser
   * atualizada nos dois lados de toda criação e reparentagem. O primeiro
   * esquecimento deixaria uma conta com filhas aceitando lançamento.
   *
   * É **diferente de `tipo`**. `tipo` é declaração, editável; `folha` é fato da
   * estrutura. Uma conta marcada ANALITICA que ganhou filhas deixa de ser folha
   * na mesma hora, e é `folha` que decide quem recebe lançamento.
   */
  folha: z.boolean(),

  nivel: z.number().int(),
  tipo: tipoContaContabilSchema,
  grupo: grupoContaContabilSchema,
  natureza: naturezaContaContabilSchema,
  demonstrativo: demonstrativoContaContabilSchema,
  exigeObra: z.boolean(),
  observacoes: z.string(),
});

export type ContaContabil = z.infer<typeof contaContabilSchema>;

export const listaContasContabeisSchema = respostaListaSchema(contaContabilSchema);
export type ListaContasContabeis = z.infer<typeof listaContasContabeisSchema>;

/**
 * Filtros da listagem do plano de contas.
 *
 * Grupo e tipo entram como filtro porque são a forma natural de ler um plano de
 * 262 contas: "me mostre só os custos", "só as analíticas". Sem eles, a busca
 * por texto seria o único recorte.
 */
export const listarContasContabeisQuerySchema = listagemDeCadastroSchema.extend({
  /**
   * Um grupo, ou vários.
   *
   * Vários porque há recorte que não é de um grupo só: o seletor de conta do
   * insumo mostra `CUSTO` e `DESPESA` juntos — é onde o custo de obra e o gasto
   * administrativo se classificam, e nenhum dos dois sozinho serve. Na
   * querystring são chaves repetidas (`?grupo=CUSTO&grupo=DESPESA`), que é como
   * o Fastify já entrega lista.
   *
   * O `z.union` mantém quem passa um só funcionando sem mudar nada.
   */
  grupo: z.union([grupoContaContabilSchema, z.array(grupoContaContabilSchema).min(1)]).optional(),
  tipo: tipoContaContabilSchema.optional(),
  // `filtroAtivoSchema` e não `z.coerce.boolean()`: coerção trata a **string**
  // `'false'` como verdadeira, e o filtro "não exige obra" traria justamente as
  // contas que exigem.
  exigeObra: filtroAtivoSchema,
  /**
   * Só as folhas da árvore.
   *
   * É o recorte de quem vai **lançar**: só folha recebe lançamento
   * (`.spec/010`). Filtrar no servidor, e não no cliente, é o que garante a
   * resposta certa quando o plano não cabe numa página.
   */
  folha: filtroAtivoSchema,
});

export type ListarContasContabeisQuery = z.infer<typeof listarContasContabeisQuerySchema>;

export const COLUNAS_ORDENAVEIS_CONTA_CONTABIL = [
  'codigoInterno',
  'codigoReduzido',
  'descricao',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Plano de contas padrão                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Importação do plano padrão numa conta que já existe.
 *
 * `sobrescrever` decide o que fazer com um código que já está lá: por padrão a
 * conta existente é preservada e a do catálogo é pulada. Verdadeiro, a
 * descrição e a classificação do catálogo passam por cima — o código reduzido e
 * o id **nunca** mudam, porque há lançamento apontando para eles.
 */
export const importarPlanoPadraoSchema = z.object({
  sobrescrever: z.boolean().prefault(false),
});

export type ImportarPlanoPadrao = z.infer<typeof importarPlanoPadraoSchema>;

export const resultadoPlanoPadraoSchema = z.object({
  criadas: z.number().int(),
  atualizadas: z.number().int(),
  ignoradas: z.number().int(),
  total: z.number().int(),
});

export type ResultadoPlanoPadrao = z.infer<typeof resultadoPlanoPadraoSchema>;

/* -------------------------------------------------------------------------- */
/* Histórico bancário                                                         */
/* -------------------------------------------------------------------------- */

/**
 * A **natureza do movimento** na conta corrente: o que foi aquele lançamento.
 *
 * Pagamento, Recebimento, Baixa de título, Compensação de cheque, Juros,
 * Desconto. Não confundir com o histórico financeiro, que é a forma de
 * pagamento — por onde o dinheiro andou.
 */
export const criarHistoricoBancarioSchema = z.object({
  descricao: descricaoSchema,
  ativo: z.boolean().prefault(true),
});

export type CriarHistoricoBancario = z.infer<typeof criarHistoricoBancarioSchema>;
export const atualizarHistoricoBancarioSchema = criarHistoricoBancarioSchema;
export type AtualizarHistoricoBancario = z.infer<typeof atualizarHistoricoBancarioSchema>;

export const historicoBancarioSchema = z.object({
  ...camposDeResposta,
  descricao: z.string(),
});

export type HistoricoBancario = z.infer<typeof historicoBancarioSchema>;

export const listaHistoricosBancariosSchema = respostaListaSchema(historicoBancarioSchema);
export type ListaHistoricosBancarios = z.infer<typeof listaHistoricosBancariosSchema>;

export const listarHistoricosBancariosQuerySchema = listagemDeCadastroSchema;
export type ListarHistoricosBancariosQuery = z.infer<typeof listarHistoricosBancariosQuerySchema>;

export const COLUNAS_ORDENAVEIS_HISTORICO_BANCARIO = ['descricao', 'createdAt'] as const;

/* -------------------------------------------------------------------------- */
/* Histórico financeiro                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A **forma de pagamento** da baixa, e o efeito dela sobre o saldo do título.
 *
 * Dinheiro, Cartão, Pix, Cheque, Boleto. Não confundir com o histórico
 * bancário, que é a natureza do movimento.
 *
 * `acao` é o campo que decide dinheiro: quase toda forma de pagamento
 * `DIMINUI_SALDO` — quita parte do devido —, mas juros e multa `AUMENTA_SALDO`,
 * e há lançamento que `MANTEM_SALDO` (`.spec/006`, seção 5). Sem esse campo,
 * cada baixa precisaria carregar o sinal, e o mesmo histórico somaria numa tela
 * e subtrairia em outra.
 */
export const criarHistoricoFinanceiroSchema = z.object({
  descricao: descricaoSchema,
  /**
   * Natureza de movimento que esta forma costuma gerar. **Opcional**: nem toda
   * forma de pagamento corresponde a uma única natureza, e obrigar a escolher
   * produziria vínculo inventado. Informado, poupa o usuário de escolher os
   * dois no lançamento.
   */
  historicoBancarioId: uuidSchema.nullable().prefault(null),
  acao: acaoSaldoTituloSchema,
  ativo: z.boolean().prefault(true),
});

export type CriarHistoricoFinanceiro = z.infer<typeof criarHistoricoFinanceiroSchema>;
export const atualizarHistoricoFinanceiroSchema = criarHistoricoFinanceiroSchema;
export type AtualizarHistoricoFinanceiro = z.infer<typeof atualizarHistoricoFinanceiroSchema>;

export const historicoFinanceiroSchema = z.object({
  ...camposDeResposta,
  descricao: z.string(),
  historicoBancarioId: uuidSchema.nullable(),
  /** Desnormalizado na leitura, para a listagem não pedir o bancário à parte. */
  historicoBancarioDescricao: z.string().nullable(),
  acao: acaoSaldoTituloSchema,
});

export type HistoricoFinanceiro = z.infer<typeof historicoFinanceiroSchema>;

export const listaHistoricosFinanceirosSchema = respostaListaSchema(historicoFinanceiroSchema);
export type ListaHistoricosFinanceiros = z.infer<typeof listaHistoricosFinanceirosSchema>;

export const listarHistoricosFinanceirosQuerySchema = listagemDeCadastroSchema;
export type ListarHistoricosFinanceirosQuery = z.infer<
  typeof listarHistoricosFinanceirosQuerySchema
>;

export const COLUNAS_ORDENAVEIS_HISTORICO_FINANCEIRO = ['descricao', 'createdAt'] as const;

/* -------------------------------------------------------------------------- */
/* Conta corrente                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Conta corrente da empresa do grupo.
 *
 * O payload **não tem saldo**, e isso é de propósito. O saldo existe na tabela
 * (`.spec/004`, seção 3.1: "conta corrente guarda saldo"), mas quem o mantém é
 * a movimentação — a conta nasce zerada e o cadastro nunca o escreve. Aceitá-lo
 * aqui abriria a porta para um `curl` gravar saldo que não bate com o extrato,
 * e o job `recalcula-saldos` passaria a alertar todo dia.
 */
export const criarContaCorrenteSchema = z.object({
  /** Empresa do grupo dona da conta — `pessoas` com `is_empresa`. */
  empresaId: uuidSchema,
  descricao: descricaoSchema,
  ativo: z.boolean().prefault(true),
});

export type CriarContaCorrente = z.infer<typeof criarContaCorrenteSchema>;
export const atualizarContaCorrenteSchema = criarContaCorrenteSchema;
export type AtualizarContaCorrente = z.infer<typeof atualizarContaCorrenteSchema>;

export const contaCorrenteSchema = z.object({
  ...camposDeResposta,
  empresaId: uuidSchema,
  /** Desnormalizado na leitura, para a listagem não pedir a empresa à parte. */
  empresaNome: z.string().nullable(),
  descricao: z.string(),
  /**
   * Saldo atual, **somente leitura**.
   *
   * Volta como string decimal, nunca como number: ponto flutuante em cálculo
   * financeiro perde centavos de forma silenciosa (`dinheiroSchema`).
   */
  saldoAtual: dinheiroSchema,
});

export type ContaCorrente = z.infer<typeof contaCorrenteSchema>;

export const listaContasCorrentesSchema = respostaListaSchema(contaCorrenteSchema);
export type ListaContasCorrentes = z.infer<typeof listaContasCorrentesSchema>;

export const listarContasCorrentesQuerySchema = listagemDeCadastroSchema.extend({
  /** Filtro por empresa: a tela de uma empresa não deve listar a conta de outra. */
  empresaId: uuidSchema.optional(),
});

export type ListarContasCorrentesQuery = z.infer<typeof listarContasCorrentesQuerySchema>;

export const COLUNAS_ORDENAVEIS_CONTA_CORRENTE = ['descricao', 'createdAt'] as const;
